/**
 * Health Monitor
 * Surveille la santé de l'application en temps réel
 * Détecte les problèmes avant qu'ils ne causent des crashs
 */

import { LIMITS } from '@/config/features'
import { logger } from '@/utils/productionLogger'

interface HealthMetrics {
  realtimeConnections: number
  failedConnections: number
  renderCount: number
  lastCheck: number
}

interface HealthStatus {
  healthy: boolean
  issues: string[]
  metrics: HealthMetrics
}

export class HealthMonitor {
  private metrics: HealthMetrics = {
    realtimeConnections: 0,
    failedConnections: 0,
    renderCount: 0,
    lastCheck: Date.now(),
  }
  
  /**
   * Vérifie la santé globale de l'application
   */
  checkHealth(): HealthStatus {
    const issues: string[] = []
    
    // Vérifier le nombre de connexions WebSocket
    if (this.metrics.realtimeConnections > LIMITS.MAX_REALTIME_CONNECTIONS) {
      issues.push(
        `⚠️ Too many WebSocket connections: ${this.metrics.realtimeConnections}/${LIMITS.MAX_REALTIME_CONNECTIONS}`
      )
    }
    
    // Vérifier les échecs de connexion
    if (this.metrics.failedConnections > 10) {
      issues.push(
        `⚠️ Too many failed connections: ${this.metrics.failedConnections}`
      )
    }
    
    // Vérifier les re-renders excessifs
    if (this.metrics.renderCount > LIMITS.MAX_RENDERS_PER_10S) {
      issues.push(
        `⚠️ Excessive re-renders: ${this.metrics.renderCount} in 10s`
      )
    }
    
    const healthy = issues.length === 0
    
    if (!healthy) {
      logger.warn('[HealthMonitor] Health check failed', { issues, metrics: this.metrics })
    }
    
    return {
      healthy,
      issues,
      metrics: { ...this.metrics },
    }
  }
  
  /**
   * Incrémente le compteur de connexions
   */
  incrementConnections() {
    this.metrics.realtimeConnections++
    logger.debug('[HealthMonitor] Connection opened', {
      total: this.metrics.realtimeConnections
    })
    
    // Vérifier si on dépasse la limite
    if (this.metrics.realtimeConnections > LIMITS.MAX_REALTIME_CONNECTIONS) {
      logger.error('[HealthMonitor] Connection limit exceeded!', {
        current: this.metrics.realtimeConnections,
        limit: LIMITS.MAX_REALTIME_CONNECTIONS
      })
    }
  }
  
  /**
   * Décrémente le compteur de connexions
   */
  decrementConnections() {
    this.metrics.realtimeConnections = Math.max(0, this.metrics.realtimeConnections - 1)
    logger.debug('[HealthMonitor] Connection closed', {
      remaining: this.metrics.realtimeConnections
    })
  }
  
  /**
   * Enregistre un échec de connexion
   */
  recordFailure() {
    this.metrics.failedConnections++
    logger.warn('[HealthMonitor] Connection failure recorded', {
      total: this.metrics.failedConnections
    })
  }
  
  /**
   * Enregistre un re-render
   */
  recordRender() {
    this.metrics.renderCount++
    
    // Reset du compteur toutes les 10 secondes
    const now = Date.now()
    if (now - this.metrics.lastCheck > 10000) {
      if (this.metrics.renderCount > LIMITS.MAX_RENDERS_PER_10S) {
        logger.warn('[HealthMonitor] High render count detected', {
          count: this.metrics.renderCount,
          period: '10s'
        })
      }
      
      this.metrics.renderCount = 0
      this.metrics.lastCheck = now
    }
  }
  
  /**
   * Réinitialise les métriques
   */
  reset() {
    this.metrics = {
      realtimeConnections: 0,
      failedConnections: 0,
      renderCount: 0,
      lastCheck: Date.now(),
    }
    logger.info('[HealthMonitor] Metrics reset')
  }
  
  /**
   * Retourne les métriques actuelles
   */
  getMetrics(): HealthMetrics {
    return { ...this.metrics }
  }
}

// Instance globale du health monitor
export const healthMonitor = new HealthMonitor()
